"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // Focus ring suave alinhado ao manifesto (4px rose-10%) em vez do
  // ring-2 sólido — emocional e premium.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:shadow-[0_0_0_4px_rgba(225,29,72,0.18)]",
  {
    variants: {
      variant: {
        default:
          // Hover desce ao vinho (manifesto) e a sombra usa tom rosé.
          "bg-[#E11D48] text-white hover:bg-[#881337] active:scale-95 shadow-[0_10px_30px_rgba(225,29,72,0.25)]",
        wine:
          "bg-[#881337] text-white hover:bg-[#6B0F2A] active:scale-95 shadow-[0_10px_30px_rgba(136,19,55,0.22)]",
        outline:
          // Texto em vinho (#881337), borda rosé suave, hover suave.
          "border border-[#FFE4E6] text-[#881337] bg-white hover:bg-[#FFF8FA] hover:text-[#E11D48] hover:border-[#E11D48] active:scale-95",
        ghost:
          "text-[#881337] bg-transparent hover:bg-[#FFE4E6] hover:text-[#E11D48] active:scale-95",
        secondary:
          "bg-[#FFE4E6] text-[#881337] hover:bg-[#FECDD3] active:scale-95",
        gold:
          // Premium: bg dourado, texto vinho (legibilidade + identidade).
          "bg-[#F59E0B] text-[#881337] hover:bg-[#E11D48] hover:text-white active:scale-95 shadow-[0_10px_30px_rgba(245,158,11,0.30)]",
        destructive:
          "bg-red-600 text-white hover:bg-red-700 active:scale-95",
        link: "text-[#E11D48] underline-offset-4 hover:underline hover:text-[#881337] p-0 h-auto",
      },
      size: {
        default: "h-11 px-6 py-2",
        sm: "h-9 px-4 text-xs",
        lg: "h-14 px-8 text-base",
        xl: "h-16 px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            <span>{children}</span>
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
